// @flow
import React from 'react';
import _ from 'lodash';
import Spinner from '../common/Spinner';
import classNames from 'classnames';
import WithComments from '../comments/WithComments';
import { CROP, TEXT_DRAWINGS, ASSET_SCALE_INFO } from '../store/Constants';
import StoreListener from '../store/StoreListener';
import Rect from '../live-draw/svg/Rect';
import Store from '../store/Store';
import Crop from '../drawing/Crop';
import TextModel from '../drawing/Text';
import Text from '../live-draw/svg/Text';
import Button from '../common/Button';
import t from '../i18n/I18n';
import { ThemeSetup } from '../themes/ThemeSetup';
import './ScaleableAssetView.css';
import WithAiData from '../components/WithAiData';
import { KeyCode } from '../util/Constants';

const debug = require('../debug/debug')('wc:assets:ScaleableAssetView');

type Props = {
  src: string,
  loading: boolean,
  preventScroll: boolean,
  cropMode: boolean,
  isExpandedMode: boolean,
  onScaleChange: Function,
  onClick: Function,
  renderComments: Function,
  children: any,
  onImageLoaded: Function,
  CROP: Object,
  TEXT_DRAWINGS: Object,
  ASSET_SCALE_INFO: Object,
  widthOfCol: number
};

type State = {
  loading: boolean,
  naturalWidth: number,
  naturalHeight: number,
  isDraggingCrop: boolean,
  center: {
    x: number,
    y: number,
    scrollWidth: number,
    scrollHeight: number
  }
};

const storeItems = [CROP, TEXT_DRAWINGS, ASSET_SCALE_INFO];

@StoreListener(storeItems)
export default class ScaleableAssetView extends React.Component<Props, State> {
  static defaultProps = {
    renderComments: _.noop,
    onClick: _.noop,
    preventScroll: true
  };

  _didDrag: boolean;
  _firstRun: boolean;
  _hostNode: ?HTMLDivElement;
  _ignoreClickEvent: boolean;
  _isDragging: boolean;
  _img: ?HTMLImageElement;
  _initialScale: number;
  _lastX: number;
  _lastY: number;
  _node: ?HTMLDivElement;
  _suspiciousHeightDetectedCount: number;
  textDrawingMode: boolean = false;
  constructor(props: Props) {
    super(props);

    this.state = {
      loading: true,
      naturalWidth: 1,
      naturalHeight: 1,
      isDraggingCrop: false,
      center: {
        x: 0,
        y: 0,
        scrollWidth: 0,
        scrollHeight: 0
      },
      pdfMargin: {
        marginLeft: '0px',
        marginTop: '0px'
      }
    };

    this.init();
    window.addEventListener('resize', this.onWindowResize);
    this.deb_createPdfEmbed = _.debounce(this.createPdfEmbed, 500);
  }
  componentDidMount() {
    const {isComaparedImgAvailable, isLeftImg, src} = this.props
    document.addEventListener('keydown', this.keyDown);
    if(src && isComaparedImgAvailable) {
      this.props.isComaparedImgAvailable(true, isLeftImg);
    }
    if (this.isPdfFile()) {
      this.onPdfLoad();
    }
  }
  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.asset.getName() !== this.props.asset.getName()) {
      this.init();
    }
    if (nextProps[ASSET_SCALE_INFO].scale !== this.props[ASSET_SCALE_INFO].scale && this._node) {
      const { scrollTop, scrollLeft, scrollWidth, scrollHeight, clientWidth, clientHeight } = this._node;

      const center = {
        x: scrollLeft + clientWidth / 2,
        y: scrollTop + clientHeight / 2,
        scrollWidth,
        scrollHeight
      };
      this.setState({ center });
    }
  }
  createPdfEmbedWrapper(scale) {
    !this.state.loading && this.setState({ loading: true });
    this.deb_createPdfEmbed(scale);
  }

  createPdfEmbed(scale) {
    const wrapper = document.getElementById('pdf-data');
    const data = document.getElementById('data');
    if (data) {
      const obj = data.cloneNode(true);
      obj.src = `${this.props.src}#toolbar=0&navpanes=0&scrollbar=none&zoom=${Math.floor(scale * 100)}`;
      const { width, height } = this.getScaledSize();
      obj.width = width;
      obj.height = height;
      obj.style.marginTop = this.getEmbedPdfStyles(scale).marginTop;
      wrapper.removeChild(data);
      wrapper.appendChild(obj);
      this.setState({ loading: false });
      this.props.setLoading();
      this.setState({
        pdfMargin: this.getScaledMargin(width, height)
      });
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { center } = this.state;
    if (center !== prevState.center && this._node) {
      const { clientWidth, clientHeight, scrollWidth, scrollHeight } = this._node;
      this._node.scrollLeft = (center.x / center.scrollWidth) * scrollWidth - clientWidth / 2;
      this._node.scrollTop = (center.y / center.scrollHeight) * scrollHeight - clientHeight / 2;
    }
    if (this.props.loading && prevProps.loading !== this.props.loading) {
      this.setState({ loading: this.props.loading });
    }
    if (this.isPdfFile()) {
      if (
        this.props.src != prevProps.src ||
        this.props.height !== prevProps.height ||
        this.props.width !== prevProps.width
      ) {
        this.onPdfLoad();
        this._scale = false;
        this.createPdfEmbedWrapper(this.props[ASSET_SCALE_INFO].scale);
      } else if (prevProps[ASSET_SCALE_INFO].scale !== this.props[ASSET_SCALE_INFO].scale) {
        this.createPdfEmbedWrapper(this.props[ASSET_SCALE_INFO].scale);
      }

      if(this.props.isFullScreen != prevProps.isFullScreen) {
        setTimeout(() => {
          const {width, height} = this.getScaledSize();
          this.setState({
            pdfMargin : this.getScaledMargin(width, height)
          })
        }, 250);
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
    document.removeEventListener('keydown', this.keyDown);
  }
  keyDown = (e: KeyboardEvent) => {
    if (e.target.tagName === 'INPUT') {
    } else {
      if (e.keyCode === KeyCode.DELETE) {
        this.deleteTextDrawing();
      } else {
        if (e.keyCode === KeyCode.BACKSPACE && navigator.appVersion.indexOf('Mac') !== -1) {
          this.deleteTextDrawing();
        }
      }
    }
  };

  isPdfFile = () => this.props.asset.getName().includes('.pdf');

  deleteTextDrawing = () => {
    const textDrawings = this.props[TEXT_DRAWINGS];
    const textDrawingsArray: TextModel[] = _.values(textDrawings);
    if (!textDrawingsArray.length) {
      return null;
    }
    textDrawingsArray.map((textDrawing) => {
      textDrawing.active ? this.onDeleteTextDrawingClick(textDrawing) : null;
    });
  };

  init = () => {
    this._initialScale = 1;
    this._firstRun = true;
    this._suspiciousHeightDetectedCount = 0;
  };

  onLoad = () => {
    debug('[onLoad()]');
    this.onResize();
  };

  onWindowResize = () => {
    if (this.isPdfFile()) {
      this.createPdfEmbed(this.props[ASSET_SCALE_INFO].scale)
    }
    (_.debounce(() => {
      this.init();
      this.onResize();
    }, 250))();
  }

  onResize = () => {
    const { isExpandedMode } = this.props;
    debug('[onResize()] begin');
    if (!this._img) {
      debug('[onResize()] _img not ready. return.');
      return;
    }

    if (!this._node) {
      debug('[onResize()] _node not ready. return.');
      return;
    }

    const { naturalWidth, naturalHeight } = this._img;
    const { clientWidth, clientHeight } = this._node;

    const TOP_BAR_HEIGHT = window.gs.topBarHeight;
    const BOTTOM_BAR_HEIGHT = isExpandedMode ? 60 : 40;
    const DELTA = 200;
    const targetHeight = window.innerHeight - TOP_BAR_HEIGHT - BOTTOM_BAR_HEIGHT - DELTA;
    if (clientHeight < targetHeight) {
      this._suspiciousHeightDetectedCount++;
      debug('[onResize()] suspicious clientHeight: ', clientHeight);
      if (this._suspiciousHeightDetectedCount < 10) {
        debug(`[onResize()] try again in 500ms. return.`);
        return setTimeout(() => this.onResize(), 500);
      } else {
        debug(
          `[onResize()] [warn] _suspiciousHeightDetectedCount: ${this._suspiciousHeightDetectedCount}. Continuing execution; layout may be off.`
        );
      }
    }

    debug(`[onResize()] sizes: ${JSON.stringify({ naturalWidth, naturalHeight, clientWidth, clientHeight })}`);
    if (!(naturalWidth && naturalHeight && clientWidth && clientHeight)) {
      debug('[onResize()] some/all sizes not ready. Try again in 500ms. return.');
      return setTimeout(() => this.onResize(), 500);
    }

    let scale = Math.min(clientWidth / naturalWidth, clientHeight / naturalHeight);
    debug('[onResize()] scale: ', scale);

    this.setState({
      loading: false,
      naturalWidth,
      naturalHeight
    });

    if (this._firstRun) {
      debug('[onResize()] _firstRun. calling props.onScaleChange()');
      this._firstRun = false;
      this._initialScale = scale;
      if (scale > 1) scale = 1;
      this.props.onScaleChange(scale, true);
    }
    if (this.props.onImageLoaded) {
      this.props.onImageLoaded();
    }
    if (this.props.updateSvgImage) {
      this.props.updateSvgImage();
    }
  };

  onPdfLoad = () => {
    const { isExpandedMode } = this.props;

    if (!this._node) {
      debug('[onResize()] _node not ready. return.');
      return;
    }

    const { clientWidth, clientHeight } = this._node;
    const naturalWidth = this.props.width > 0 ? this.props.width : clientWidth;
    const naturalHeight = this.props.height > 0 ? this.props.height : clientHeight;

    this.setState({
      naturalWidth,
      naturalHeight,
      loading: false
    });

    if (this._node && !this._scale) {
      this._scale = true;
      this._node.scrollLeft = 0;
      this._node.scrollTop = 0;
    }

    if (this._firstRun) {
      const initialScale = Math.min(
        naturalHeight > 6000 ? (6000 / naturalHeight) * 0.74 : 0.74,
        naturalWidth > 4000 ? (4000 / naturalWidth) * 0.74 : 0.74
      );
      const maxMinScale = {
        max: Math.min((8000 / naturalHeight) * 0.74, (8000 / naturalWidth) * 0.74, 3),
        min: initialScale / 2
      };

      debug('[onResize()] _firstRun. calling props.onScaleChange()');
      if (this.props.width || this.props.height) {
        this._firstRun = false;
      }
      this.props.onScaleChange(initialScale, true, maxMinScale);
    }
  };

  onMouseDown = (e: MouseEvent) => {
    if (!_.includes(['img', 'svg'], e.target.nodeName.toLowerCase())) {
      return;
    }

    e.preventDefault();
    debug('[onMouseDown()] starting drag');
    this._isDragging = true;
    this._didDrag = false;
    this._lastX = e.clientX;
    this._lastY = e.clientY;
  };

  onMouseMove = (e: MouseEvent) => {
    if (!_.includes(['img', 'svg'], e.target.nodeName.toLowerCase()) || !this._isDragging) {
      return;
    }

    const diffX = e.clientX - this._lastX;
    const diffY = e.clientY - this._lastY;

    if (!(diffX || diffY)) {
      return;
    }

    debug('[onMouseMove()] dragging');
    this._didDrag = true;
    this._node.scrollLeft -= diffX;
    this._node.scrollTop -= diffY;

    this._lastX = e.clientX;
    this._lastY = e.clientY;
  };

  onMouseUp = () => {
    if (!this._isDragging) {
      return;
    }

    debug('[onMouseUp()] ending drag');
    this._isDragging = false;

    if (this._didDrag) {
      debug('[onMouseUp()] this._didDrag is set. Ignoring click event');
      this._didDrag = false;
      this._ignoreClickEvent = true;
      setTimeout(() => (this._ignoreClickEvent = false), 100);
    }
  };

  onClick = (e: MouseEvent) => {
    if (this._ignoreClickEvent) {
      debug('[onClick()] this._ignoreClickEvent is set. Not calling props.onClick');
      return;
    }

    debug('[onClick()] Calling props.onClick');
    this.props.onClick(e);
  };

  getScaledSize = () => {
    const scale = this.props[ASSET_SCALE_INFO].scale;
    const { naturalWidth, naturalHeight } = this.state;
    if (this.isPdfFile()) {
      return {
        width: Math.round(((naturalWidth * 100) / 74) * scale),
        height: Math.round(((naturalHeight * 100) / 74) * scale)
      };
    }
    return {
      width: Math.round(naturalWidth * scale)-1,
      height: Math.round(naturalHeight * scale)
    };
  };
  getScaledMargin = (width, height) => {
    const {widthOfCol} = this.props;
    const rect = this._node ? this._node.getBoundingClientRect() : {};
    let left = widthOfCol / 2 - width / 2;
    let top = rect.height / 2 - height / 2;
    const leftMargin = left > 0 ? left : 0;
    const topMargin = top > 0 ? top : 0;
    return { marginTop: topMargin + 'px', marginLeft: leftMargin + 'px' };
  };
  getPosition = () => {
    const rect = this._node ? this._node.getBoundingClientRect() : {};
    const { width, height } = this.getScaledSize();
    const x = Math.max((rect.width - width) / 2, 0);
    const y = Math.max((rect.height - height) / 2, 0);

    return {
      transform: `translate(${x}px, ${y}px)`
    };
  };

  onUpdateShape = (data: Object, id: any, Class: any) => {
    const scale = this.props[ASSET_SCALE_INFO].scale;
    const { naturalWidth, naturalHeight } = this.state;
    const { x, y, width, height } = data;
    const scaledX = x / scale;
    const scaledY = y / scale;
    const scaledWidth = width / scale;
    const scaledHeight = height / scale;

    if (scaledX < 0) return;
    if (scaledY < 0) return;
    if (scaledWidth < 0) return;
    if (scaledHeight < 0) return;
    if (scaledX + scaledWidth > naturalWidth) return;
    if (scaledY + scaledHeight > naturalHeight) return;

    return new Class(scaledX, scaledY, scaledWidth, scaledHeight, id);
  };

  onDeleteTextDrawingClick = (textDrawing: Object) => {
    const textDrawings = { ...this.props[TEXT_DRAWINGS] };
    delete textDrawings[textDrawing.id];
    Store.put(TEXT_DRAWINGS, textDrawings);
  };

  setActiveTextDrawing = (textDrawing: ?Object) => {
    const textDrawings = { ...this.props[TEXT_DRAWINGS] };
    _.values(textDrawings).forEach((t) => (t.active = textDrawing && t.id === textDrawing.id));
    Store.put(TEXT_DRAWINGS, textDrawings);
  };

  renderCrop = () => {
    const scale = this.props[ASSET_SCALE_INFO].scale;
    const { cropMode } = this.props;
    const crop: Crop = this.props[CROP];
    if (!cropMode) {
      return null;
    }

    const data = crop.toSvgRect(scale);

    const onUpdateShape = (data) => {
      const newCrop = this.onUpdateShape(data, crop.id, Crop);
      if (newCrop) {
        Store.put(CROP, newCrop);
      }
    };

    const onStopDrag = () => {
      this.setState({ isDraggingCrop: false });
      let dragAbleRect = document.getElementsByName('Draggable')[0].getElementsByTagName('rect')[0];
      let dragAbleElement = document.getElementsByName('Draggable')[0].getElementsByTagName('rect')[1];
      dragAbleElement.setAttribute('x', dragAbleRect.getAttribute('x') - 5);
      dragAbleElement.setAttribute('y', dragAbleRect.getAttribute('y') - 5);
    };

    const props = {
      data,
      isActive: true,
      svgNode: this._hostNode,
      onUpdateShape,
      onResize: onUpdateShape,
      onDrag: onUpdateShape,
      onDragStop: onStopDrag
    };

    const { x, y, width, height } = data;

    const gridLines = [
      `M ${x} ${y + (height * 1) / 3} `,
      `L ${x + width} ${y + (height * 1) / 3} `,
      `M ${x} ${y + (height * 2) / 3} `,
      `L ${x + width} ${y + (height * 2) / 3} `,
      `M ${x + (width * 1) / 3} ${y} `,
      `L ${x + (width * 1) / 3} ${y + height} `,
      `M ${x + (width * 2) / 3} ${y} `,
      `L ${x + (width * 2) / 3} ${y + height}`
    ].join('');

    return (
      <g className="crop-markup">
        <g fill="#0005" className="dark">
          <rect x={0} y={0} width="100%" height={Math.max(y, 0)} />
          <rect x={0} y={y} width={x} height={height} />
          <rect x={x + width} y={y} height={height} style={{ width: `calc(100% - ${x + width}px)` }} />
          <rect x={0} y={y + height} width="100%" style={{ height: `calc(100% - ${y}px - ${height}px)` }} />
        </g>
        <g onMouseDown={() => this.setState({ isDraggingCrop: true })}>
          <Rect {...props} />
        </g>
        <path fill="none" stroke="#000" d={gridLines} />
        <foreignObject className="info-object" x={x} y={y} width={width} height={height}>
          <div className="info-wrapper">
            <div className={classNames('info', { dragging: this.state.isDraggingCrop })}>
              <div>
                <span>
                  x: <b>{Math.round(data.x / scale)}px</b>
                </span>
              </div>
              <div>
                <span>
                  y: <b>{Math.round(data.y / scale)}px</b>
                </span>
              </div>
              <div>
                <span>
                  {t('WIDTH')}: <b>{Math.round(data.width / scale)}px</b>
                </span>
              </div>
              <div>
                <span>
                  {t('HEIGHT')}: <b>{Math.round(data.height / scale)}px</b>
                </span>
              </div>
            </div>
          </div>
        </foreignObject>
      </g>
    );
  };

  renderText = () => {
    const scale = this.props[ASSET_SCALE_INFO].scale;

    const textDrawings = this.props[TEXT_DRAWINGS];
    const textDrawingsArray: TextModel[] = _.values(textDrawings);
    if (!textDrawingsArray.length) {
      return null;
    }

    const nodes = textDrawingsArray.map((textDrawing) => {
      const data = textDrawing.toSvgRect(scale);

      const onUpdateShape = (data) => {
        const newTextDrawing = this.onUpdateShape(data, textDrawing.id, TextModel);
        if (!newTextDrawing) {
          return;
        }

        newTextDrawing.text = data.text || textDrawing.text;
        newTextDrawing.font = textDrawing.font;

        const drawings = { ...textDrawings };
        drawings[newTextDrawing.id] = newTextDrawing;
        Store.put(TEXT_DRAWINGS, drawings);
      };

      const onStopDrag = () => {
        let dragAbleRect = document.getElementsByName('Draggable')[0].getElementsByTagName('foreignObject')[0];
        let dragAbleElement = document.getElementsByName('Draggable')[0].getElementsByTagName('rect')[0];
        if (dragAbleRect && dragAbleElement) {
          dragAbleElement.setAttribute('x', dragAbleRect.getAttribute('x') - 5);
          dragAbleElement.setAttribute('y', dragAbleRect.getAttribute('y') - 5);
        }
      };

      const props = {
        data,
        isActive: textDrawing.active,
        svgNode: this._hostNode,
        onUpdateShape,
        onResize: onUpdateShape,
        onDrag: onUpdateShape,
        setActiveShapeId: (id) => this.setActiveTextDrawing(id ? textDrawing : null),
        onDragStop: onStopDrag
      };

      const { x, y, width } = data;
      debug('Text Drawing', textDrawing.id);
      return (
        <g key={textDrawing.id}>
          <Text {...props} />
          {textDrawing.active ? (
            <foreignObject className="button-wrapper" width="16" height="16" x={x + width - 16} y={y}>
              <Button
                className="delete"
                icon={ThemeSetup.getImageURL('close_search.svg')}
                onClick={() => this.onDeleteTextDrawingClick(textDrawing)}
              />
            </foreignObject>
          ) : null}
        </g>
      );
    });

    return <g className="text-markup">{nodes}</g>;
  };

  renderMarkup = () => {
    const { cropMode } = this.props;
    const textDrawings = this.props[TEXT_DRAWINGS];

    const textDrawingsArray: TextModel[] = _.values(textDrawings);
    this.textDrawingMode = textDrawingsArray.length > 0 ? true : false;
    if (!(cropMode || textDrawingsArray.length)) {
      return null;
    }

    return (
      <svg
        className="markup"
        onClick={(e) => {
          if (e.target === e.currentTarget) this.setActiveTextDrawing(null);
        }}>
        {this.renderCrop()}
        {this.renderText()}
      </svg>
    );
  };

  getEmbedPdfStyles = (scale) => {
    let styles;
    if (window.navigator.userAgent.indexOf('Chrome') !== -1) {
      styles = {
        marginTop: `-${((2 * 100) / 74) * scale}px`
      };
    } else if (window.navigator.userAgent.indexOf('Firefox') !== -1) {
      styles = {
        marginTop: '-42px'
      };
    } else if (window.navigator.userAgent.indexOf('Safari')) {
      styles = {
        marginTop: `-${((18 * 100) / 74) * scale}px`
      };
    }
    return styles;
  };

  previewFullImage = () => {
    const { width, height } = this.getScaledSize();
    const clientHeight = this._node && this._node.clientHeight;
    const clientWidth = this._node && this._node.clientWidth;

    if (height > clientHeight && height !== 1) {
      this._hostNode.style.top = 0;
      if (width > clientWidth && width !== 0) {
        this._hostNode.style.left = 0;
      } else {
        this._hostNode.style.left = 'unset';
      }
    } else if (width > clientWidth && width !== 0) {
      this._hostNode.style.left = 0;
      if (height > clientHeight && height !== 1) {
        this._hostNode.style.top = 0;
      } else {
        this._hostNode.style.top = 'unset';
      }
    } else {
      if (this._hostNode !== undefined) {
        this._hostNode.style.top = 'unset';
        this._hostNode.style.left = 'unset';
      }
    }
  };

  render() {
    const scale = this.props[ASSET_SCALE_INFO].scale;
    const { src, renderComments, children, preventScroll } = this.props;
    const { loading } = this.state;

    const { width, height } = this.getScaledSize();
    const position = this.getPosition();
    const baseline = this._initialScale === scale;
    const noScroll = preventScroll && baseline;
    const { marginTop, marginLeft } = this.getScaledMargin(width, height);
    let styles = {width, height };
    const showWrapper = this._node ? this._node.getBoundingClientRect().width ? true : false : false;
    // if (this.isPdfFile()) {
    //   styles = {
    //     marginLeft: this.state.pdfMargin.marginLeft,
    //     marginTop: this.state.pdfMargin.marginTop
    //   };
    // }
    const props = {
      name: 'ScaleableAssetView',
      ref: (node) => (this._node = node),
      className: classNames({ baseline, 'no-scroll': noScroll }),
      onClick: this.onClick
    };

    if (noScroll) {
      props.onMouseDown = (e) => e.preventDefault();
    } else {
      props.onMouseDown = this.onMouseDown;
      props.onMouseMove = this.onMouseMove;
      props.onMouseUp = this.onMouseUp;
    }

    this.previewFullImage();

    return (
      <div {...props}>
        {loading && !showWrapper ? (
          <div className="loading">
            <Spinner large />
          </div>
        ) : null}
        <div
          id="wrapper"
          className={`wrapper ${this.isPdfFile() ? 'pdf-wrapper' : ''}`}
          style={showWrapper ?  styles : {display: 'none'}}
          ref={(node) => (this._hostNode = node)}>
          {this.isPdfFile() ? (
            <div id="pdf-data">
              <embed
                style={{ ...this.getEmbedPdfStyles(scale), marginBottom: '-10px' }}
                ref={(node) => (this._img = node)}
                id="data"
                src={`${this.props.src}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0`}
                type="application/pdf"
                width={width}
                height={height}></embed>
            </div>
          ) : (
            <img
              ref={(node) => (this._img = node)}
              src={src}
              className={classNames({ loading })}
              onLoad={this.onLoad}
              draggable={false}
            />
          )}
          {loading ? null : this.renderMarkup()}
          {loading ? null : children}
          {loading ? null : renderComments()}
        </div>
      </div>
    );
  }
}

const getRootNode = (component) => (component ? component._node : null);
const getHostNode = (component) => (component ? component._hostNode : null);
const getImgNode = (component) => (component ? component._img : null);
export const ScaleableAssetViewWithComments = WithComments(getRootNode, getHostNode)(ScaleableAssetView);
export const AIData = WithAiData(getRootNode, getHostNode, getImgNode)(ScaleableAssetView);
